// src/CatalogueModal.js
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, ShoppingCart, ChevronDown, ChevronUp } from 'lucide-react';

const ICONES_CAT = {
  'Panneaux':              { icon: '☀️', color: '#f59e0b' },
  'Onduleurs':             { icon: '⚡', color: '#3b82f6' },
  'Batteries':             { icon: '🔋', color: '#10b981' },
  'Materiel electrique':   { icon: '🔌', color: '#8b5cf6' },
  'Pompes Solaires DC':    { icon: '💧', color: '#06b6d4' },
  'Pompes Solaires AC':    { icon: '💧', color: '#0891b2' },
  'Clim Solaire DC':       { icon: '❄️', color: '#0ea5e9' },
  'Clim Solaire ACDC':     { icon: '⚡', color: '#7c3aed' },
  'Clim Solaire Mobile':   { icon: '❄️', color: '#0284c7' },
  'Chauffe-eaux Solaires': { icon: '☀️', color: '#ea580c' },
  'Lampadaires':           { icon: '💡', color: '#eab308' },
  'Projecteurs':           { icon: '🔦', color: '#ec4899' },
  'Prestations':           { icon: '🛠️', color: '#78350f' },
  'Régulateurs':           { icon: '⚙️', color: '#0369a1' },
  'Regulateurs':           { icon: '⚙️', color: '#0369a1' },
  'Générateur solaire':    { icon: '⚡', color: '#d97706' },
  'Generateur solaire':    { icon: '⚡', color: '#d97706' },
  'Supports de fixation':  { icon: '🔧', color: '#64748b' },
  'Autre':                 { icon: '🧊', color: '#14b8a6' },
};
const getCatLabel = (cat) => (ICONES_CAT[cat]?.icon || '📦') + ' ' + cat;
const getCatColor = (cat) => ICONES_CAT[cat]?.color || '#6b7280';
const getCatIcon = (cat) => ICONES_CAT[cat]?.icon || '📦';

const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));

const CatalogueModal = ({ onClose, onAddItem }) => {
  const [produits, setProduits] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState([]);
  const [categorie, setCategorie] = useState('tous');
  const [expanded, setExpanded] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [added, setAdded] = useState({});

  const fetchProduits = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('catalogue_produits').select('*').eq('actif', true).order('categorie').order('nom');
    const combined = data || [];
    setProduits(combined);
    setFiltered(combined);
    const cats = [...new Set(combined.map(p => p.categorie).filter(Boolean))].sort();
    setCategories(cats);
    setLoading(false);
  }, []);

  useEffect(() => { fetchProduits(); }, [fetchProduits]);

  useEffect(() => {
    let result = produits;
    if (categorie !== 'tous') {
      result = result.filter(p => p.categorie === categorie);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(p =>
        p.nom?.toLowerCase().includes(q) ||
        p.ref?.toLowerCase().includes(q) ||
        p.categorie?.toLowerCase().includes(q) ||
        p.puissance?.toLowerCase().includes(q)
      );
    }
    setFiltered(result);
  }, [search, categorie, produits]);

  const handleAdd = (produit) => {
    const qty = quantities[produit.id] || 1;
    onAddItem({
      description: produit.nom + (produit.puissance && produit.puissance !== '-' ? ' — ' + produit.puissance : '') + '\n' + (produit.description || '').replace(/▪/g, '-'),
      quantity: qty,
      unitPrice: produit.prix,
      image: produit.image_url || null,
      categorie: produit.categorie || '',
      _offert: produit.categorie === 'Prestations' && (!produit.prix || produit.prix === 0) ? true : false,
    });
    setAdded(prev => ({ ...prev, [produit.id]: true }));
    setTimeout(() => setAdded(prev => ({ ...prev, [produit.id]: false })), 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 70, padding: '16px',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        style={{
          background: 'white', borderRadius: '16px', width: '100%',
          maxWidth: '860px', maxHeight: '90vh', display: 'flex',
          flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{
          background: 'linear-gradient(135deg, #1e40af, #3b82f6)',
          padding: '20px 24px', borderRadius: '16px 16px 0 0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ color: 'white', fontWeight: 700, fontSize: '18px' }}>
              📦 Catalogue Produits
            </div>
            <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '12px', marginTop: '2px' }}>
              {filtered.length} produit{filtered.length > 1 ? 's' : ''} disponible{filtered.length > 1 ? 's' : ''}
            </div>
          </div>
          <button onClick={onClose}
            onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.2)'}
            onMouseLeave={e => e.currentTarget.style.background='transparent'}
            style={{ background: 'transparent', border: 'none', color: 'white',
              cursor: 'pointer', borderRadius: '6px', padding: '6px',
              transition: 'background 0.15s', display: 'flex' }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: '16px 24px 0', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ position: 'relative', marginBottom: '12px' }}>
            <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%',
              transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input
              type="text"
              placeholder="Rechercher un produit, une référence, une puissance..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
              style={{
                width: '100%', boxSizing: 'border-box',
                border: '1px solid #e5e7eb', borderRadius: '8px',
                padding: '10px 12px 10px 36px', fontSize: '14px', outline: 'none',
              }}
              onFocus={e => e.target.style.borderColor='#3b82f6'}
              onBlur={e => e.target.style.borderColor='#e5e7eb'}
            />
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', paddingBottom: '12px' }}>
            {[{key: 'tous', label: 'Tous', color: '#6b7280'}, ...categories.map(cat => ({key: cat, label: getCatLabel(cat), color: getCatColor(cat)}))].map(cat => (
              <button key={cat.key} onClick={() => setCategorie(cat.key)}
                style={{
                  background: categorie === cat.key ? cat.color : '#f3f4f6',
                  color: categorie === cat.key ? 'white' : '#6b7280',
                  border: 'none', borderRadius: '999px', padding: '5px 12px',
                  fontSize: '12px', fontWeight: 500, cursor: 'pointer',
                  transition: 'all 0.15s',
                }}>
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 24px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px' }}>
              Chargement du catalogue...
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px' }}>
              <div style={{ fontSize: '36px', marginBottom: '8px' }}>📭</div>
              <div>Aucun produit trouvé</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {filtered.map(produit => (
                <div key={produit.id}
                  style={{
                    border: '1px solid #e5e7eb', borderRadius: '10px',
                    overflow: 'hidden', transition: 'border-color 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor='#93c5fd'}
                  onMouseLeave={e => e.currentTarget.style.borderColor='#e5e7eb'}
                >
                  <div style={{
                    display: 'flex', alignItems: 'center', padding: '10px 14px',
                    gap: '10px', background: 'white',
                  }}>
                    {/* Miniature photo */}
                    {produit.image_url ? (
                      <img src={produit.image_url} alt={produit.nom}
                        style={{ width: '44px', height: '44px', objectFit: 'contain',
                          borderRadius: '6px', border: '1px solid #e5e7eb',
                          background: '#f8fafc', flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: '44px', height: '44px', borderRadius: '6px',
                        border: '1px solid #f3f4f6', background: '#f8fafc',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '22px', flexShrink: 0 }}>
                        {getCatIcon(produit.categorie)}
                      </div>
                    )}
                    <span style={{
                      background: getCatColor(produit.categorie),
                      color: 'white', borderRadius: '6px', padding: '3px 8px',
                      fontSize: '10px', fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap',
                    }}>
                      {produit.categorie}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '14px', color: '#1f2937',
                        whiteSpace: 'normal', overflow: 'visible', wordBreak: 'break-word' }}>
                        {produit.nom}
                      </div>
                      <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                        Réf: {produit.ref}
                        {produit.puissance_w && ` · ${produit.puissance_w}W`}
                      </div>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: '14px', color: '#1e40af',
                      flexShrink: 0, whiteSpace: 'nowrap' }}>
                      {fmt(produit.prix)} FCFA
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                      <button
                        onClick={() => setQuantities(prev => ({ ...prev, [produit.id]: Math.max(1, (prev[produit.id] || 1) - 1) }))}
                        style={{ width: '24px', height: '24px', border: '1px solid #e5e7eb',
                          borderRadius: '4px', background: 'white', cursor: 'pointer',
                          fontSize: '16px', display: 'flex', alignItems: 'center',
                          justifyContent: 'center', color: '#374151' }}>
                        −
                      </button>
                      <span style={{ width: '28px', textAlign: 'center', fontSize: '14px',
                        fontWeight: 600, color: '#1f2937' }}>
                        {quantities[produit.id] || 1}
                      </span>
                      <button
                        onClick={() => setQuantities(prev => ({ ...prev, [produit.id]: (prev[produit.id] || 1) + 1 }))}
                        style={{ width: '24px', height: '24px', border: '1px solid #e5e7eb',
                          borderRadius: '4px', background: 'white', cursor: 'pointer',
                          fontSize: '16px', display: 'flex', alignItems: 'center',
                          justifyContent: 'center', color: '#374151' }}>
                        +
                      </button>
                    </div>
                    <button onClick={() => handleAdd(produit)}
                      style={{
                        background: added[produit.id] ? '#10b981' : '#1e40af',
                        color: 'white', border: 'none', borderRadius: '8px',
                        padding: '7px 14px', fontSize: '12px', fontWeight: 600,
                        cursor: 'pointer', flexShrink: 0, transition: 'all 0.2s',
                        display: 'flex', alignItems: 'center', gap: '5px',
                        whiteSpace: 'nowrap',
                      }}>
                      {added[produit.id] ? '✓ Ajouté' : <><ShoppingCart size={13} /> Ajouter</>}
                    </button>
                    {produit.description && (
                      <button onClick={() => setExpanded(expanded === produit.id ? null : produit.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer',
                          color: '#9ca3af', padding: '2px', flexShrink: 0,
                          display: 'flex', alignItems: 'center' }}>
                        {expanded === produit.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                    )}
                  </div>
                  <AnimatePresence>
                    {expanded === produit.id && produit.description && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        style={{ overflow: 'hidden' }}
                      >
                        <div style={{
                          padding: '12px 14px', background: '#f8fafc',
                          borderTop: '1px solid #e5e7eb',
                          fontSize: '12px', color: '#374151', lineHeight: '1.6',
                          whiteSpace: 'pre-wrap',
                        }}>
                          {produit.description}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{
          padding: '12px 24px', borderTop: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ fontSize: '12px', color: '#9ca3af' }}>
            Cliquez sur ▾ pour voir la description complète
          </span>
          <button onClick={onClose}
            onMouseEnter={e => { e.currentTarget.style.background='#f3f4f6'; }}
            onMouseLeave={e => { e.currentTarget.style.background='white'; }}
            style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px',
              padding: '8px 20px', fontSize: '13px', cursor: 'pointer',
              fontWeight: 500, transition: 'all 0.15s' }}>
            Fermer
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CatalogueModal;
